import React, { useState, useEffect } from 'react';
import { X, ChartLineUp, Sparkle, CheckCircle, Spinner, Copy, ArrowRight, Target, Eye, Heart, Lightning, Megaphone, Rocket, ArrowsClockwise } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api from '@/utils/api';
import { getUser } from '@/utils/auth';

const ScoreContentModal = ({ isOpen, onClose, onSuccess, initialContent = '' }) => {
  const user = getUser();
  const [step, setStep] = useState('input'); // 'input', 'analyzing', 'result'
  const [loading, setLoading] = useState(false);
  const [generatingVariations, setGeneratingVariations] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [aiTwin, setAiTwin] = useState(null);
  
  const [formData, setFormData] = useState({
    content: initialContent,
    platform: 'instagram',
    include_variations: false
  });

  // Fetch assistant settings on mount
  useEffect(() => {
    const fetchAiTwin = async () => {
      try {
        const response = await api.get('/auth/aitwin');
        if (response.data?.configured) {
          setAiTwin(response.data);
        }
      } catch (error) {
        // Assistant not configured, that's okay
      }
    };
    if (isOpen) {
      fetchAiTwin();
    }
  }, [isOpen]);

  const platforms = [
    { id: 'instagram', label: 'Instagram', icon: '📸' },
    { id: 'tiktok', label: 'TikTok', icon: '🎵' },
    { id: 'youtube', label: 'YouTube', icon: '▶️' },
    { id: 'email', label: 'Email', icon: '✉️' },
    { id: 'sales_page', label: 'Sales Page', icon: '💰' }
  ];

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  const getScoreBarColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getScoreIcon = (name) => {
    switch (name) {
      case 'Hook Strength': return <Lightning size={16} weight="fill" />;
      case 'Clarity': return <Eye size={16} weight="fill" />;
      case 'Curiosity': return <Sparkle size={16} weight="fill" />;
      case 'Emotional Pull': return <Heart size={16} weight="fill" />;
      case 'Call to Action': return <Megaphone size={16} weight="fill" />;
      default: return <Target size={16} weight="fill" />;
    }
  };

  const handleSubmit = async (withVariations = false) => {
    if (!formData.content.trim()) {
      toast.error('Please enter your content to analyze');
      return;
    }

    setStep('analyzing');
    setLoading(true);

    try {
      const response = await api.post('/content/score', {
        ...formData,
        include_variations: withVariations,
        // Pass assistant context if available
        niche: aiTwin?.business_niche,
        target_audience: aiTwin?.target_audience
      });
      setResult(response.data);
      setStep('result');
      if (onSuccess) onSuccess();
      toast.success('Your content has been scored!');
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(error.response?.data?.detail || 'Daily limit reached. Upgrade for unlimited access!');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to score content');
      }
      setStep('input');
    } finally {
      setLoading(false);
    }
  };

  // Generate variations for already-scored content
  const handleGenerateVariations = async () => {
    setGeneratingVariations(true);
    try {
      const response = await api.post('/content/score', {
        content: formData.content,
        platform: formData.platform,
        include_variations: true,
        niche: aiTwin?.business_niche,
        target_audience: aiTwin?.target_audience
      });
      setResult(response.data);
      toast.success('Viral variations generated!');
    } catch (error) {
      toast.error('Failed to generate variations');
    } finally {
      setGeneratingVariations(false);
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleClose = () => {
    setStep('input');
    setResult(null);
    setFormData({
      content: '',
      platform: 'instagram'
    });
    onClose();
  };

  const handleTryAnother = () => {
    setStep('input');
    setResult(null);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="score-content-modal">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-2xl paygbot-modal-shell bg-slate-900 rounded-2xl shadow-2xl border border-white/10"
        >
          {/* Header */}
          <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <ChartLineUp size={20} className="text-white" weight="fill" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Score My Content</h2>
                <p className="text-sm text-slate-400">Get AI feedback before you post</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="close-score-modal"
            >
              <X size={18} className="text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Input Step */}
            {step === 'input' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Content Input */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Paste your content to analyze *
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    data-testid="content-input"
                    placeholder="Paste your post, caption, script, email, or sales copy here..."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={6}
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    {formData.content.length} characters
                  </p>
                </div>

                {/* Platform Selector */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Platform
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {platforms.map((platform) => (
                      <button
                        key={platform.id}
                        onClick={() => setFormData({...formData, platform: platform.id})}
                        data-testid={`platform-${platform.id}`}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm transition-all ${
                          formData.platform === platform.id
                            ? 'bg-blue-500/20 border-blue-500/50 border text-blue-400'
                            : 'bg-white/5 border-white/10 border text-white hover:bg-white/10'
                        }`}
                      >
                        <span>{platform.icon}</span>
                        <span>{platform.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* AI Assistant Context Banner */}
                {aiTwin && (
                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3 flex items-center space-x-3">
                    <Sparkle size={18} className="text-purple-400" weight="fill" />
                    <span className="text-sm text-purple-300">
                      Using your assistant context: {aiTwin.business_niche}
                    </span>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  onClick={() => handleSubmit(false)}
                  disabled={!formData.content.trim()}
                  data-testid="score-content-submit"
                  className="w-full py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:from-slate-600 disabled:to-slate-600 text-white font-medium rounded-xl transition-all flex items-center justify-center space-x-2"
                >
                  <ChartLineUp size={20} weight="fill" />
                  <span>Analyze My Content</span>
                  <ArrowRight size={18} />
                </button>
              </motion.div>
            )}

            {/* Analyzing Step */}
            {step === 'analyzing' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center py-12"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-4 animate-pulse">
                  <ChartLineUp size={32} className="text-white" weight="fill" />
                </div>
                <div className="flex items-center space-x-2 text-white">
                  <Spinner size={20} className="animate-spin" />
                  <span>Analyzing your content...</span>
                </div>
                <p className="text-sm text-slate-400 mt-2">Checking hook, clarity, curiosity, emotional pull, and CTA</p>
              </motion.div>
            )}

            {/* Result Step */}
            {step === 'result' && result && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Overall Score Card */}
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6 text-center">
                  <p className="text-sm text-slate-400 mb-2">Overall Score</p>
                  <div className={`text-5xl font-bold ${getScoreColor(result.overall_score)}`}>
                    {result.overall_score}
                  </div>
                  <p className={`text-lg font-medium mt-1 ${getScoreColor(result.overall_score)}`}>
                    {result.overall_label}
                  </p>
                </div>

                {/* Sub-Scores */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-slate-300">Breakdown</h4>
                  {result.sub_scores.map((score, index) => (
                    <motion.div
                      key={score.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white/5 border border-white/10 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className={getScoreColor(score.score)}>
                            {getScoreIcon(score.name)}
                          </span>
                          <span className="text-white text-sm font-medium">{score.name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`text-sm font-medium ${getScoreColor(score.score)}`}>
                            {score.score}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            score.score >= 80 ? 'bg-green-500/20 text-green-400' :
                            score.score >= 60 ? 'bg-yellow-500/20 text-yellow-400' :
                            score.score >= 40 ? 'bg-orange-500/20 text-orange-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {score.label}
                          </span>
                        </div>
                      </div>
                      {/* Progress Bar */}
                      <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${score.score}%` }}
                          transition={{ delay: index * 0.1 + 0.2, duration: 0.5 }}
                          className={`h-full ${getScoreBarColor(score.score)} rounded-full`}
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* AI Feedback */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center space-x-2">
                    <Sparkle size={16} className="text-blue-400" weight="fill" />
                    <span>AI Feedback</span>
                  </h4>
                  <p className="text-sm text-slate-300 leading-relaxed">{result.feedback}</p>
                </div>

                {/* Suggested Improvements */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-slate-300">Suggested Improvements</h4>
                  
                  {/* Improved Hook */}
                  <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Lightning size={16} className="text-yellow-400" weight="fill" />
                        <span className="text-sm font-medium text-yellow-400">Improved Hook</span>
                      </div>
                      <button
                        onClick={() => handleCopy(result.suggestions.improved_hook, 'hook')}
                        data-testid="copy-improved-hook"
                        className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs transition-all ${
                          copiedField === 'hook'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-white/10 text-white hover:bg-white/20'
                        }`}
                      >
                        {copiedField === 'hook' ? <CheckCircle size={12} /> : <Copy size={12} />}
                        <span>{copiedField === 'hook' ? 'Copied!' : 'Copy'}</span>
                      </button>
                    </div>
                    <p className="text-white text-sm">{result.suggestions.improved_hook}</p>
                  </div>

                  {/* Stronger CTA */}
                  <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Megaphone size={16} className="text-green-400" weight="fill" />
                        <span className="text-sm font-medium text-green-400">Stronger Call to Action</span>
                      </div>
                      <button
                        onClick={() => handleCopy(result.suggestions.stronger_cta, 'cta')}
                        data-testid="copy-stronger-cta"
                        className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs transition-all ${
                          copiedField === 'cta'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-white/10 text-white hover:bg-white/20'
                        }`}
                      >
                        {copiedField === 'cta' ? <CheckCircle size={12} /> : <Copy size={12} />}
                        <span>{copiedField === 'cta' ? 'Copied!' : 'Copy'}</span>
                      </button>
                    </div>
                    <p className="text-white text-sm">{result.suggestions.stronger_cta}</p>
                  </div>

                  {/* Rewritten Section */}
                  <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Sparkle size={16} className="text-purple-400" weight="fill" />
                        <span className="text-sm font-medium text-purple-400">Rewritten Section</span>
                      </div>
                      <button
                        onClick={() => handleCopy(result.suggestions.rewritten_section, 'rewritten')}
                        data-testid="copy-rewritten-section"
                        className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs transition-all ${
                          copiedField === 'rewritten'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-white/10 text-white hover:bg-white/20'
                        }`}
                      >
                        {copiedField === 'rewritten' ? <CheckCircle size={12} /> : <Copy size={12} />}
                        <span>{copiedField === 'rewritten' ? 'Copied!' : 'Copy'}</span>
                      </button>
                    </div>
                    <p className="text-white text-sm">{result.suggestions.rewritten_section}</p>
                  </div>

                  {/* Viral Version (if available) */}
                  {result.suggestions.viral_version && (
                    <div className="bg-gradient-to-br from-pink-500/10 to-red-500/10 border border-pink-500/30 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <Rocket size={16} className="text-pink-400" weight="fill" />
                          <span className="text-sm font-medium text-pink-400">Viral Version</span>
                        </div>
                        <button
                          onClick={() => handleCopy(result.suggestions.viral_version, 'viral')}
                          data-testid="copy-viral-version"
                          className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs transition-all ${
                            copiedField === 'viral'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-white/10 text-white hover:bg-white/20'
                          }`}
                        >
                          {copiedField === 'viral' ? <CheckCircle size={12} /> : <Copy size={12} />}
                          <span>{copiedField === 'viral' ? 'Copied!' : 'Copy'}</span>
                        </button>
                      </div>
                      <p className="text-white text-sm whitespace-pre-wrap">{result.suggestions.viral_version}</p>
                    </div>
                  )}

                  {/* Variations (if available) */}
                  {result.suggestions.variations?.length > 0 && (
                    <div className="space-y-2">
                      <h5 className="text-sm font-medium text-slate-300 flex items-center space-x-2">
                        <ArrowsClockwise size={16} className="text-cyan-400" />
                        <span>Content Variations</span>
                      </h5>
                      {result.suggestions.variations.map((variation, index) => (
                        <div key={index} className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-cyan-400">Variation {index + 1}</span>
                            <button
                              onClick={() => handleCopy(variation, `variation-${index}`)}
                              className={`text-xs flex items-center space-x-1 ${
                                copiedField === `variation-${index}` ? 'text-green-400' : 'text-slate-400 hover:text-white'
                              }`}
                            >
                              {copiedField === `variation-${index}` ? <CheckCircle size={12} /> : <Copy size={12} />}
                              <span>Copy</span>
                            </button>
                          </div>
                          <p className="text-white text-sm">{variation}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Improvement Actions */}
                {!result.suggestions.viral_version && (
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <h4 className="text-sm font-medium text-slate-300 mb-3">Want to make it even better?</h4>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={handleGenerateVariations}
                        disabled={generatingVariations}
                        data-testid="generate-viral-btn"
                        className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-pink-500/20 to-red-500/20 border border-pink-500/30 text-pink-400 rounded-lg text-sm font-medium hover:from-pink-500/30 hover:to-red-500/30 transition-all disabled:opacity-50"
                      >
                        {generatingVariations ? (
                          <Spinner size={16} className="animate-spin" />
                        ) : (
                          <Rocket size={16} weight="fill" />
                        )}
                        <span>Generate Viral Version</span>
                      </button>
                      <button
                        onClick={handleGenerateVariations}
                        disabled={generatingVariations}
                        data-testid="create-variations-btn"
                        className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 text-cyan-400 rounded-lg text-sm font-medium hover:from-cyan-500/30 hover:to-blue-500/30 transition-all disabled:opacity-50"
                      >
                        {generatingVariations ? (
                          <Spinner size={16} className="animate-spin" />
                        ) : (
                          <ArrowsClockwise size={16} />
                        )}
                        <span>Create 3 Variations</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleTryAnother}
                    className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all flex items-center justify-center space-x-2"
                  >
                    <ChartLineUp size={18} />
                    <span>Score Another</span>
                  </button>
                  <button
                    onClick={handleClose}
                    className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-medium rounded-xl transition-all flex items-center justify-center space-x-2"
                  >
                    <CheckCircle size={18} weight="fill" />
                    <span>Done</span>
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ScoreContentModal;
